import { ChatMessages, Icons } from "@storytell/cp-client";
import { richTextToProseMirror } from "@storytell/rich-text";
import { produce } from "solid-js/store";
import { accumulateAggregatedMetrics } from "../../types";
import type {
  SetStreamingStore,
  StreamingBlock,
  StreamingBlockId,
  StreamingBlockMetrics,
  StreamingMessageId,
  StreamingOperation,
  StreamingOperationId,
  StreamingTask,
  StructuredSearchResult,
} from "../streamingTypes";

// ------------------------------------------------------------
// BLOCK HANDLERS
// ------------------------------------------------------------

const getBlockMetrics = (message: ChatMessages.Message): StreamingBlockMetrics | undefined => {
  if (
    message.ingressTokens > 0 ||
    message.egressTokens > 0 ||
    message.ingressBytes > 0 ||
    message.egressBytes > 0 ||
    message.ingressCredits > 0 ||
    message.egressCredits > 0
  ) {
    return {
      ingressTokens: Number(message.ingressTokens),
      egressTokens: Number(message.egressTokens),
      ingressBytes: Number(message.ingressBytes),
      egressBytes: Number(message.egressBytes),
      ingressCredits: Number(message.ingressCredits),
      egressCredits: Number(message.egressCredits),
    };
  }
};

export const processBlockStart = (
  setStore: SetStreamingStore,
  message: ChatMessages.Message,
  eventTimestamp?: Date,
) => {
  if (message.data.case === "messageTaskData") return processTaskStart(setStore, message, eventTimestamp);

  if (message.data.case !== "messageStartData") {
    console.error(new Error(`Expected messageStartData for block start, got ${message.data.case}`));
    return;
  }

  const {
    id,
    role,
    blockId: _blockId,
    operationId: _operationId,
    activity,
    data: { value: data },
  } = message;

  const blockId = _blockId as StreamingBlockId;
  const operationId = _operationId as StreamingOperationId;
  const messageId = id as StreamingMessageId;

  const streamingBlock: StreamingBlock = {
    isComplete: false,
    type: "block",
    blockId,
    operationId,
    messageId,
    role,
    activity,
    feature: data.feature,
    messageType: data.messageType,
    title: data.title,
    deltas: [],
    finalContent: undefined,
    searchResults: undefined,
    webSearchResults: undefined,
    conceptResults: undefined,
    structuredResults: undefined,
    imageResults: undefined,
    metrics: getBlockMetrics(message),
  };

  setStore(
    produce((store) => {
      const operation = store.operations[operationId];
      if (operation && !operation.blocks.includes(blockId)) {
        operation.blocks.push(blockId);
      }
      store.blocks[blockId] = streamingBlock;
    }),
  );
};

export const processBlockDelta = (setStore: SetStreamingStore, message: ChatMessages.Message) => {
  if (message.data.case === "messageTaskData") return processTaskDelta(setStore, message);

  if (message.data.case !== "messageDeltaData" && message.data.case !== "messageData") {
    console.error(new Error(`Expected messageDeltaData, got ${message.data.case}`));
    return;
  }

  const {
    blockId: _blockId,
    data: { value: data },
  } = message;
  const blockId = _blockId as StreamingBlockId;

  const metrics = getBlockMetrics(message);

  setStore(
    produce((store) => {
      const block = store.blocks[blockId];
      if (!block) {
        console.error(new Error(`Block not found for delta: ${blockId}`));
        return;
      }
      if (block.type !== "block") {
        console.error(new Error(`Block is a task, not a block: ${blockId}`));
        return;
      }

      if (message.data.case === "messageDeltaData") {
        block.deltas.push({ text: message.data.value.text });
      }

      if (metrics) {
        block.metrics = metrics;

        const linkedMessage = store.messages[block.messageId];
        if (linkedMessage) {
          const featureTokens =
            message.ingressCredits > 0 || message.egressCredits > 0 || message.creditsTotal > 0
              ? {
                  ingressCredits: Number(message.ingressCredits || 0),
                  egressCredits: Number(message.egressCredits || 0),
                  totalCredits: Number(message.creditsTotal || 0),
                }
              : undefined;

          linkedMessage.aggregateMetrics = accumulateAggregatedMetrics(
            linkedMessage.aggregateMetrics,
            Number(message.ingressTokens),
            Number(message.egressTokens),
            Number(message.ingressBytes),
            Number(message.egressBytes),
            featureTokens,
          );

          const chat = store.chats[linkedMessage.chatId];
          if (chat) {
            chat.aggregateMetrics = accumulateAggregatedMetrics(
              chat.aggregateMetrics,
              Number(message.ingressTokens),
              Number(message.egressTokens),
              Number(message.ingressBytes),
              Number(message.egressBytes),
              featureTokens,
            );
          }
        }
      }
    }),
  );
};

export const processBlockStop = (setStore: SetStreamingStore, message: ChatMessages.Message, eventTimestamp?: Date) => {
  if (message.data.case === "messageTaskData") return processTaskStop(setStore, message, eventTimestamp);

  if (message.data.case !== "messageData" && message.data.case !== undefined) {
    console.error(new Error(`Expected messageData or undefined, got ${message.data.case}`));
    return;
  }

  const { blockId: _blockId } = message;
  const blockId = _blockId as StreamingBlockId;

  setStore(
    produce((store) => {
      const block = store.blocks[blockId];
      if (!block) {
        console.error(new Error(`Block not found for stop: ${blockId}`));
        return;
      }
      if (block.type !== "block") {
        console.error(new Error(`Block is a task, not a block: ${blockId}`));
        return;
      }

      block.isComplete = true;
      if (message.data.case === "messageData") {
        const data = message.data.value;

        if (data.content.case === "text") {
          block.finalContent = { type: "text", text: data.content.value };
        } else if (data.content.case === "richText") {
          block.finalContent = { type: "richText", richText: richTextToProseMirror(data.content.value) };
        }

        if (data.searchResults) {
          block.searchResults = data.searchResults;
        }

        if (data.webSearchResults) {
          block.webSearchResults = data.webSearchResults;
        }

        if (data.imageResults) {
          block.imageResults = data.imageResults;
        }

        if (data.conceptResults) {
          block.conceptResults = data.conceptResults;
        }

        // Handle structured search results from backend
        // Backend sends raw data with headers and rows arrays
        if (data.structuredResults) {
          // Convert protobuf StructuredDataResult to StructuredSearchResult
          const pbResult = data.structuredResults;
          block.structuredResults = {
            assetId: pbResult.assetId || "",
            assetName: pbResult.assetName || "",
            headers: pbResult.headers,
            rows: pbResult.rows?.map((row) => row.values || []),
            queryIndex: pbResult.queryIndex,
            totalQueries: pbResult.totalQueries,
            rowCount: pbResult.rowCount,
            isCompletion: pbResult.isCompletion,
            duration: pbResult.duration ? Number(pbResult.duration.seconds) : undefined,
            assetCount: pbResult.assetCount,
            queryReason: pbResult.queryReason,
            query: pbResult.query,
          };
        } else if (block.title?.includes("Structured Data") || block.activity?.includes("Structured Data")) {
          // Fallback to parsing from content if no structured results
          const assetName =
            block.title?.replace("Structured Data Query Results", "").trim() ||
            block.activity?.replace("Structured Data Query Results", "").trim() ||
            "Unknown Asset";

          // Parse markdown table from content if available
          const markdownTable = data.content.case === "text" ? data.content.value : undefined;

          // Try to extract result count from the content
          let resultCount = 0;
          if (markdownTable) {
            // Count rows in markdown table (lines minus header and separator)
            const lines = markdownTable.split("\n").filter((line) => line.trim() && line.includes("|"));
            resultCount = Math.max(0, lines.length - 2); // Subtract header and separator line
          }

          block.structuredResults = {
            assetId: "",
            assetName,
            resultCount,
            markdownTable,
          };
        }

        // --- METRICS FINALIZED (creditStatement) ---
        if (data.creditStatement) {
          const creditStatement = data.creditStatement;
          const balance = creditStatement.balance ? Number(creditStatement.balance) : undefined;

          let totalFinalCredits = 0;
          let totalFinalIngressCredits = 0;
          let totalFinalEgressCredits = 0;
          let totalFinalIngressTokens = 0;
          let totalFinalEgressTokens = 0;

          if (creditStatement.debits) {
            for (const debit of creditStatement.debits) {
              if (debit.metrics?.metrics) {
                totalFinalIngressTokens += Number(debit.metrics.metrics.ingressTokens) || 0;
                totalFinalEgressTokens += Number(debit.metrics.metrics.egressTokens) || 0;
              }
              if (
                debit.metrics?.featureTokens &&
                debit.metrics.featureTokens.type === ChatMessages.FeatureTokenType.FINAL
              ) {
                totalFinalCredits += Number(debit.metrics.featureTokens.totalCredits || 0);
                totalFinalIngressCredits += Number(debit.metrics.featureTokens.ingressCredits || 0);
                totalFinalEgressCredits += Number(debit.metrics.featureTokens.egressCredits || 0);
              }
            }
          }

          // Overwrite chat-level aggregateMetrics with finalized totals
          const linkedMessage = store.messages[block.messageId];
          if (linkedMessage) {
            const chat = store.chats[linkedMessage.chatId];
            if (chat) {
              chat.aggregateMetrics = {
                ...chat.aggregateMetrics,
                totalCredits: totalFinalCredits,
                ingressCredits: totalFinalIngressCredits,
                egressCredits: totalFinalEgressCredits,
                ingressTokens: totalFinalIngressTokens,
                egressTokens: totalFinalEgressTokens,
                balance,
                lastUpdated: new Date(),
              };
            }
            // Also update message-level aggregateMetrics
            linkedMessage.aggregateMetrics = {
              ...linkedMessage.aggregateMetrics,
              totalCredits: totalFinalCredits,
              ingressCredits: totalFinalIngressCredits,
              egressCredits: totalFinalEgressCredits,
              ingressTokens: totalFinalIngressTokens,
              egressTokens: totalFinalEgressTokens,
              balance,
              lastUpdated: new Date(),
            };
          }
        }

        // --- METRICS LEGACY (metrics array) ---
        let totalFeatureCredits = 0;
        let totalIngressCredits = 0;
        let totalEgressCredits = 0;
        let isFinalTokenType = false;
        if (data.metrics) {
          for (const metric of data.metrics) {
            if (metric.featureTokens) {
              // Convert BigInt to Number for credits (safe for credit values)
              totalFeatureCredits += Number(metric.featureTokens.totalCredits || 0);
              totalIngressCredits += Number(metric.featureTokens.ingressCredits || 0);
              totalEgressCredits += Number(metric.featureTokens.egressCredits || 0);
              if (metric.featureTokens.type === ChatMessages.FeatureTokenType.FINAL) {
                isFinalTokenType = true;
              }
            }
          }
          // Only update if no creditStatement was processed (avoid double processing)
          if (totalFeatureCredits > 0 && !data.creditStatement) {
            const linkedMessage = store.messages[block.messageId];
            if (linkedMessage) {
              const chat = store.chats[linkedMessage.chatId];
              if (chat) {
                chat.aggregateMetrics = {
                  ...chat.aggregateMetrics,
                  totalCredits: isFinalTokenType
                    ? totalFeatureCredits
                    : (chat.aggregateMetrics.totalCredits || 0) + totalFeatureCredits,
                  ingressCredits: isFinalTokenType
                    ? totalIngressCredits
                    : (chat.aggregateMetrics.ingressCredits || 0) + totalIngressCredits,
                  egressCredits: isFinalTokenType
                    ? totalEgressCredits
                    : (chat.aggregateMetrics.egressCredits || 0) + totalEgressCredits,
                  lastUpdated: new Date(),
                };
              }
              linkedMessage.aggregateMetrics = {
                ...linkedMessage.aggregateMetrics,
                totalCredits: isFinalTokenType
                  ? totalFeatureCredits
                  : (linkedMessage.aggregateMetrics.totalCredits || 0) + totalFeatureCredits,
                ingressCredits: isFinalTokenType
                  ? totalIngressCredits
                  : (linkedMessage.aggregateMetrics.ingressCredits || 0) + totalIngressCredits,
                egressCredits: isFinalTokenType
                  ? totalEgressCredits
                  : (linkedMessage.aggregateMetrics.egressCredits || 0) + totalEgressCredits,
                lastUpdated: new Date(),
              };
            }
          }
        }
      }
    }),
  );
};

// ------------------------------------------------------------
// TASK HANDLERS
// ------------------------------------------------------------

export const processTaskStart = (setStore: SetStreamingStore, message: ChatMessages.Message, eventTimestamp?: Date) => {
  if (message.data.case !== "messageTaskData") {
    console.error(new Error(`Expected messageTaskData, got ${message.data.case}`));
    return;
  }

  const {
    id,
    role,
    blockId: _blockId,
    operationId: _operationId,
    data: { value: data },
  } = message;
  const blockId = _blockId as StreamingBlockId;
  const operationId = _operationId as StreamingOperationId;
  const messageId = id as StreamingMessageId;

  const streamingTask: StreamingTask = {
    type: "task",
    isComplete: false,
    messageId,
    blockId,
    operationId,
    role,
    taskId: data.taskId,
    taskName: data.taskName,
    progressPercent: data.progressPercent,
    currentActivity: data.currentActivity,
    status: data.status,
    dependencies: data.dependencies,
    icon: data.icon ?? Icons.Icon.UNSPECIFIED,
    durationInfo: data.durationInfo,
    errorMessage: data.errorMessage,
  };

  setStore(
    produce((store) => {
      // Special case for end_turn_v1, it doesn't have an operation
      // so we have to create a mock one
      if (!store.operations[operationId]) {
        const operation: StreamingOperation = {
          index: store.messages[messageId]?.operations.length ?? 0,
          isComplete: true,
          operationId,
          messageId,
          blocks: [],
          name: "Answer Complete",
          activity: "conversation_complete",
          startedAt: undefined,
          completedAt: eventTimestamp || new Date(),
          role: ChatMessages.Role.UNSPECIFIED,
          status: ChatMessages.OperationStatus.COMPLETED,
          task: streamingTask,
          progressMessage: undefined,
        };
        store.operations[operationId] = operation;
        store.messages[messageId]?.operations.push(operationId);
      } else {
        store.operations[operationId].task = streamingTask;
      }
    }),
  );
};

const _mutatingUpdateExistingTask = (task: StreamingTask, data: ChatMessages.MessageTaskData, isComplete?: boolean) => {
  if (data.progressPercent !== task.progressPercent) {
    task.progressPercent = data.progressPercent;
  }
  if (data.currentActivity !== task.currentActivity) {
    task.currentActivity = data.currentActivity;
  }
  if (data.status !== task.status) {
    task.status = data.status;
  }
  if (data.dependencies !== task.dependencies) {
    task.dependencies = data.dependencies;
  }
  if (data.errorMessage !== task.errorMessage) {
    task.errorMessage = data.errorMessage;
  }
  if (data.taskName !== task.taskName) {
    task.taskName = data.taskName;
  }
  if (data.taskId !== task.taskId) {
    task.taskId = data.taskId;
  }
  if (data.icon !== undefined) {
    task.icon = data.icon;
  }
  if (data.durationInfo !== task.durationInfo) {
    task.durationInfo = data.durationInfo;
  }
  if (isComplete !== undefined) {
    task.isComplete = isComplete;
  }
};
export const processTaskDelta = (
  setStore: SetStreamingStore,
  message: ChatMessages.Message,
  isComplete?: boolean,
  eventTimestamp?: Date,
) => {
  if (message.data.case !== "messageTaskData") {
    console.error(new Error(`Expected messageTaskData, got ${message.data.case}`));
    return;
  }

  const {
    blockId: _blockId,
    operationId: _operationId,
    role,
    id: _messageId,
    data: { value: data },
  } = message;
  const blockId = _blockId as StreamingBlockId;
  const operationId = _operationId as StreamingOperationId;
  const messageId = _messageId as StreamingMessageId;

  const streamingTask: StreamingTask = {
    type: "task",
    isComplete: false,
    messageId,
    blockId,
    operationId,
    role,
    taskId: data.taskId,
    taskName: data.taskName,
    progressPercent: data.progressPercent,
    currentActivity: data.currentActivity,
    status: data.status,
    dependencies: data.dependencies,
    icon: data.icon ?? Icons.Icon.UNSPECIFIED,
    durationInfo: data.durationInfo,
    errorMessage: data.errorMessage,
  };

  setStore(
    produce((store) => {
      if (!store.operations[operationId]) {
        const operation: StreamingOperation = {
          index: store.messages[messageId]?.operations.length ?? 0,
          isComplete: true,
          operationId,
          messageId,
          blocks: [],
          name: "Answer Complete",
          activity: "conversation_complete",
          startedAt: undefined,
          completedAt: eventTimestamp || new Date(),
          role: ChatMessages.Role.UNSPECIFIED,
          status: ChatMessages.OperationStatus.COMPLETED,
          task: streamingTask,
          progressMessage: undefined,
        };
        store.operations[operationId] = operation;
        store.messages[messageId]?.operations.push(operationId);
      }

      if (!store.messages[messageId]?.operations.includes(operationId)) {
        store.messages[messageId]?.operations.push(operationId);
      }

      if (!store.operations[operationId]?.task) {
        store.operations[operationId].task = streamingTask;
      } else {
        _mutatingUpdateExistingTask(store.operations[operationId].task, data, isComplete);
      }
    }),
  );
};

export const processTaskStop = (setStore: SetStreamingStore, message: ChatMessages.Message, eventTimestamp?: Date) => {
  processTaskDelta(setStore, message, true, eventTimestamp);
};
